import { Injectable, signal } from '@angular/core';

/**
 * Message types for broadcast channel communication.
 * T is the type of the value being synchronized.
 */
type BroadcastMessage<T> =
  { type: 'update'; value: T } | { type: 'request' } | { type: 'response'; value: T };

/**
 * A generic and type-safe service to synchronize a WritableSignal<T> across browser contexts using BroadcastChannel.
 * T must be serializable (i.e., can be posted via postMessage).
 */
@Injectable({ providedIn: 'root' })
export class SignalBroadcastChannelService {
  /**
   * Factory method to create a synchronized signal.
   * @param channelName Unique channel name for BroadcastChannel.
   * @param initialValue Initial value for the signal.
   * @returns An object containing the signal and a broadcastUpdate method.
   */
  createSynchronizedSignal<T>(channelName: string, initialValue: T) {
    const _signal = signal<T>(initialValue);
    const channel = new BroadcastChannel(channelName);

    channel.onmessage = (event: MessageEvent<BroadcastMessage<T>>) => {
      const msg = event.data;
      if (!msg || typeof msg !== 'object' || !('type' in msg)) return;

      switch (msg.type) {
        case 'update':
          _signal.set(msg.value);
          break;
        case 'request':
          channel.postMessage({
            type: 'response',
            value: _signal()
          });
          break;
        case 'response':
          _signal.set(msg.value);
          break;
        default:
          console.warn('Invalid msg.type');
      }
    };

    // Request the latest value from other tabs/windows
    channel.postMessage({ type: 'request' });

    /**
     * Broadcast a new value to other contexts.
     * @param value The new value to broadcast.
     */
    function broadcastUpdate(value: T): void {
      channel.postMessage({ type: 'update', value });
    }

    return {
      signal: _signal,
      broadcastUpdate
    };
  }
}
